/**
 * CREATIVE DETONATOR - Centralized Icons Module
 * v1 - Single source of truth for all icons
 *
 * Contains:
 * - Trigger icons (64 Unicode emoji icons)
 * - Format icon paths (PNG images)
 * - SVG icons for buttons and actions
 */

// ============================================================================
// TRIGGER ICONS (Unicode Emoji)
// ============================================================================

const TRIGGER_ICONS = {
  // Pattern Interrupts (A)
  A1: '&#10024;',  // Sparkle (impossible)
  A2: '&#128205;', // Pin (wrong context)
  A3: '&#128200;', // Chart (scale)
  A4: '&#9200;',   // Clock (time)
  A5: '&#128260;', // Arrows (reversal)

  // Emotional Detonators (B)
  B1: '&#128073;', // Pointing (guilt)
  B2: '&#128250;', // TV (nostalgia)
  B3: '&#127942;', // Trophy (pride)
  B4: '&#128064;', // Eyes (fear)
  B5: '&#127881;', // Party (joy)
  B6: '&#128149;', // Heart (empathy)

  // Cognitive Games (C)
  C1: '&#129513;', // Puzzle
  C2: '&#129704;', // Mirror
  C3: '&#128256;', // Frame
  C4: '&#127912;', // Art
  C5: '&#128172;', // Speech
  C6: '&#9878;',   // Scale

  // Participation Generators (D)
  D1: '&#9995;',   // Hand
  D2: '&#127922;', // Dice
  D3: '&#10062;',  // Complete
  D4: '&#127873;', // Gift

  // Story Engines (E)
  E1: '&#127916;', // Film
  E2: '&#128214;', // Book
  E3: '&#128483;', // Mic
  E4: '&#127793;', // Seed (origin)
  E5: '&#128302;', // Crystal ball

  // Symbol Systems (F)
  F1: '&#9888;',   // Warning
  F2: '&#128288;', // ABC
  F3: '&#128202;', // Chart
  F4: '&#127912;', // Art

  // Media Manipulation (G)
  G1: '&#128196;', // Page
  G2: '&#127758;', // Globe
  G3: '&#128257;', // Sequence
  G4: '&#128230;', // Package

  // Transgression Tactics (H)
  H1: '&#9888;',   // Warning
  H2: '&#9876;',   // Sword
  H3: '&#128578;', // Smile

  // Sensory Translators (I)
  I1: '&#127926;', // Musical note (synesthesia)
  I2: '&#128075;', // Waving hand (phantom sensation)
  I3: '&#127787;', // Sun behind cloud (ambient)
  I4: '&#9208;',   // Pause button (kinetic freeze)

  // Social Physics (J)
  J1: '&#128274;', // Lock (exclusion)
  J2: '&#128101;', // People (social proof)
  J3: '&#9878;',   // Balance scale (status)
  J4: '&#128065;', // Eye (witnessed)

  // Identity Provocations (K)
  K1: '&#129704;', // Mirror (ambush)
  K2: '&#128640;', // Rocket (future self)
  K3: '&#127769;', // Crescent moon (shadow)

  // Tension Architecture (L)
  L1: '&#8987;',   // Hourglass (delayed reveal)
  L2: '&#128168;', // Dash (almost moment)
  L3: '&#128167;', // Droplet (relief valve)
  L4: '&#9888;',   // Warning (productive discomfort)

  // Permission Engines (M)
  M1: '&#127891;', // Graduation cap (authority)
  M2: '&#127874;', // Birthday cake (occasion)
  M3: '&#127941;', // Medal (effort offset)
  M4: '&#128591;', // Folded hands (absolution)

  // Reality Ruptures (N)
  N1: '&#128084;', // Clapperboard (fourth wall)
  N2: '&#128295;', // Wrench (process)
  N3: '&#10060;',  // Cross mark (failure)
  N4: '&#9999;',   // Pencil (consumer creator)

  // Momentum Mechanics (O)
  O1: '&#127754;', // Wave (unstoppable force)
  O2: '&#128178;', // Dollar (first domino)
  O3: '&#128040;', // Fish (counter-current)
  O4: '&#127759;', // Globe (gravitational pull)

  // Fallback
  DEFAULT: '&#9733;' // Star
};

// ============================================================================
// FORMAT ICON PATHS (PNG Images)
// ============================================================================

const FORMAT_ICON_PATHS = {
  social: '/assets/images/formats/1-social-media.png',
  video: '/assets/images/formats/2-video-ad.png',
  print: '/assets/images/formats/3-ooh-media.png',
  ooh: '/assets/images/formats/3-ooh-media.png',
  experiential: '/assets/images/formats/4-experimental.png',
  digital: '/assets/images/formats/5-digital.png',
  campaign: '/assets/images/formats/6-full-campaign.png'
};

const FORMAT_MINI_ICON_PATHS = {
  print: '/assets/icons/format-icon-mini_0002_print-ooh.png',
  social: '/assets/icons/format-icon-mini_0000_social-media.png',
  video: '/assets/icons/format-icon-mini_0001_video.png',
  digital: '/assets/icons/format-icon-mini_0003_digital.png',
  campaign: '/assets/icons/format-icon-mini_0004_full-campaign.png',
  experiential: '/assets/icons/format-icon-mini_0005_experimental.png'
};

// ============================================================================
// SVG ICON DEFINITIONS
// ============================================================================

const SVG_ICONS = {
  // Action Icons (20x20 viewBox)
  canvasView: {
    viewBox: '0 0 20 20',
    path: `<rect x="1" y="1" width="18" height="2"/><rect x="1" y="17" width="18" height="2"/><rect x="1" y="1" width="2" height="18"/><rect x="17" y="1" width="2" height="18"/><rect x="4" y="4" width="5" height="2"/><rect x="4" y="4" width="2" height="5"/>`
  },

  storyboardGrid: {
    viewBox: '0 0 20 20',
    path: `<rect x="1" y="1" width="8" height="8"/><rect x="11" y="1" width="8" height="8"/><rect x="1" y="11" width="8" height="8"/><rect x="11" y="11" width="8" height="8"/>`
  },

  copy: {
    viewBox: '0 0 20 20',
    path: `<rect x="4" y="0" width="12" height="2"/><rect x="2" y="2" width="2" height="12"/><rect x="14" y="2" width="2" height="2"/><rect x="4" y="14" width="2" height="2"/><rect x="6" y="4" width="12" height="2"/><rect x="4" y="6" width="2" height="14"/><rect x="16" y="6" width="2" height="12"/><rect x="6" y="18" width="12" height="2"/><rect x="8" y="9" width="8" height="2"/><rect x="8" y="13" width="6" height="2"/>`
  },

  save: {
    viewBox: '0 0 20 20',
    // Pixelated 5-pointed star (traced from icon_0026_star-filled.png)
    path: `<rect x="9" y="0" width="2" height="2"/><rect x="8" y="2" width="4" height="2"/><rect x="2" y="4" width="3" height="2"/><rect x="7" y="4" width="6" height="2"/><rect x="15" y="4" width="3" height="2"/><rect x="0" y="6" width="20" height="2"/><rect x="2" y="8" width="16" height="2"/><rect x="4" y="10" width="12" height="2"/><rect x="5" y="12" width="10" height="2"/><rect x="5" y="14" width="4" height="2"/><rect x="11" y="14" width="4" height="2"/><rect x="4" y="16" width="4" height="2"/><rect x="12" y="16" width="4" height="2"/><rect x="2" y="18" width="4" height="2"/><rect x="14" y="18" width="4" height="2"/>`
  },

  delete: {
    viewBox: '0 0 20 20',
    path: `<rect x="7" y="0" width="6" height="2"/><rect x="2" y="2" width="16" height="2"/><rect x="3" y="4" width="2" height="14"/><rect x="15" y="4" width="2" height="14"/><rect x="3" y="16" width="14" height="2"/><rect x="7" y="7" width="2" height="7"/><rect x="11" y="7" width="2" height="7"/>`
  },

  bulb: {
    viewBox: '0 0 16 16',
    path: `<rect x="6" y="0" width="4" height="2"/><rect x="4" y="2" width="2" height="2"/><rect x="10" y="2" width="2" height="2"/><rect x="2" y="4" width="2" height="4"/><rect x="12" y="4" width="2" height="4"/><rect x="4" y="8" width="2" height="2"/><rect x="10" y="8" width="2" height="2"/><rect x="6" y="10" width="4" height="2"/><rect x="6" y="12" width="4" height="2"/><rect x="7" y="14" width="2" height="2"/>`
  },

  // Small Action Icons (12x12 viewBox) - stroke-based
  download: {
    viewBox: '0 0 12 12',
    path: 'M6 0v8M2 5l4 5 4-5',
    stroke: true,
    strokeWidth: 2,
    fill: 'none'
  },

  fullscreen: {
    viewBox: '0 0 12 12',
    path: `<rect x="0" y="0" width="4" height="2"/><rect x="0" y="0" width="2" height="4"/><rect x="8" y="0" width="4" height="2"/><rect x="10" y="0" width="2" height="4"/><rect x="0" y="10" width="4" height="2"/><rect x="0" y="8" width="2" height="4"/><rect x="8" y="10" width="4" height="2"/><rect x="10" y="8" width="2" height="4"/>`
  },

  refresh: {
    viewBox: '0 0 12 12',
    path: 'M11 6a5 5 0 1 1-1.5-3.5M11 2v3h-3',
    stroke: true,
    strokeWidth: 1.5,
    fill: 'none'
  },

  edit: {
    viewBox: '0 0 12 12',
    path: 'M1 11l2-6 6-4 2 2-4 6-6 2zm7.5-7.5l1 1',
    stroke: true,
    strokeWidth: 1.5,
    fill: 'none'
  },

  video: {
    viewBox: '0 0 12 12',
    path: `<rect x="0" y="2" width="8" height="8" rx="1"/><path d="M9 4l3 2-3 2V4z"/>`
  },

  // Image icon (picture frame with mountain/sun)
  image: {
    viewBox: '0 0 12 12',
    path: `<rect x="0" y="1" width="12" height="10" fill="none" stroke="currentColor" stroke-width="1"/><circle cx="3" cy="4" r="1.5"/><path d="M1 9l3-3 2 2 3-4 2 3v2H1z"/>`
  },

  // Navigation arrows
  arrowLeft: {
    viewBox: '0 0 12 12',
    path: 'M8 2L4 6l4 4',
    stroke: true,
    strokeWidth: 2,
    fill: 'none'
  },

  arrowRight: {
    viewBox: '0 0 12 12',
    path: 'M4 2l4 4-4 4',
    stroke: true,
    strokeWidth: 2,
    fill: 'none'
  },

  // Presentation Format Icons (32x32 viewBox)
  formatPrint: {
    viewBox: '0 0 32 32',
    path: `<rect x="4" y="4" width="24" height="24"/><rect x="8" y="8" width="16" height="4"/><rect x="8" y="14" width="12" height="2"/><rect x="8" y="18" width="16" height="2"/><rect x="8" y="22" width="10" height="2"/>`
  },

  formatVideo: {
    viewBox: '0 0 32 32',
    path: `<rect x="2" y="6" width="28" height="20" rx="2"/><polygon points="13,11 13,21 22,16"/>`
  },

  formatSocial: {
    viewBox: '0 0 32 32',
    path: `<rect x="4" y="4" width="24" height="24" rx="4"/><circle cx="16" cy="14" r="4"/><rect x="10" y="22" width="12" height="2"/>`
  },

  formatExperiential: {
    viewBox: '0 0 32 32',
    path: `<circle cx="16" cy="16" r="12"/><circle cx="16" cy="16" r="6"/><circle cx="16" cy="16" r="2"/>`
  },

  formatDigital: {
    viewBox: '0 0 32 32',
    path: `<rect x="4" y="4" width="24" height="18" rx="2"/><rect x="10" y="24" width="12" height="4"/>`
  },

  formatCampaign: {
    viewBox: '0 0 32 32',
    path: `<polygon points="16,2 20,12 30,12 22,18 26,28 16,22 6,28 10,18 2,12 12,12"/>`
  },

  // Format icons (outline style for presentation mode)
  formatPrintOutline: {
    viewBox: '0 0 32 32',
    path: `<rect x="4" y="4" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"/><rect x="8" y="8" width="16" height="4"/><rect x="8" y="14" width="12" height="2"/><rect x="8" y="18" width="14" height="2"/>`
  },
  formatVideoOutline: {
    viewBox: '0 0 32 32',
    path: `<rect x="2" y="6" width="28" height="20" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="12,10 12,22 22,16"/>`
  },
  formatSocialOutline: {
    viewBox: '0 0 32 32',
    path: `<rect x="8" y="2" width="16" height="28" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><rect x="14" y="26" width="4" height="2"/>`
  },
  formatExperientialOutline: {
    viewBox: '0 0 32 32',
    path: `<circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="16" cy="16" r="4"/>`
  },
  formatDigitalOutline: {
    viewBox: '0 0 32 32',
    path: `<rect x="2" y="4" width="28" height="20" fill="none" stroke="currentColor" stroke-width="2"/><rect x="10" y="24" width="12" height="4" fill="none" stroke="currentColor" stroke-width="2"/>`
  },
  formatCampaignOutline: {
    viewBox: '0 0 32 32',
    path: `<rect x="4" y="8" width="24" height="16" fill="none" stroke="currentColor" stroke-width="2"/><polygon points="16,4 20,8 12,8"/>`
  },

  // Regenerate/refresh image (16x16 frame with face pattern)
  regenerate: {
    viewBox: '0 0 16 16',
    path: `<rect x="1" y="1" width="14" height="2"/><rect x="1" y="13" width="14" height="2"/><rect x="1" y="1" width="2" height="14"/><rect x="13" y="1" width="2" height="14"/><rect x="5" y="5" width="2" height="2"/><rect x="9" y="5" width="2" height="2"/><rect x="5" y="9" width="6" height="2"/>`
  },

  // Fullscreen/preview (16x16 corner brackets)
  fullscreen16: {
    viewBox: '0 0 16 16',
    path: `<rect x="1" y="1" width="4" height="2"/><rect x="1" y="1" width="2" height="4"/><rect x="11" y="1" width="4" height="2"/><rect x="13" y="1" width="2" height="4"/><rect x="1" y="13" width="4" height="2"/><rect x="1" y="11" width="2" height="4"/><rect x="11" y="13" width="4" height="2"/><rect x="13" y="11" width="2" height="4"/>`
  },

  // Make video / play button with frame (16x16)
  makeVideo: {
    viewBox: '0 0 16 16',
    path: `<rect x="1" y="2" width="14" height="2"/><rect x="1" y="12" width="14" height="2"/><rect x="1" y="2" width="2" height="12"/><rect x="13" y="2" width="2" height="12"/><rect x="5" y="5" width="2" height="6"/><rect x="7" y="6" width="2" height="4"/><rect x="9" y="7" width="2" height="2"/>`
  },

  // Generate image placeholder (24x24 frame with face)
  generateImage: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="20" height="3"/><rect x="2" y="19" width="20" height="3"/><rect x="2" y="2" width="3" height="20"/><rect x="19" y="2" width="3" height="20"/><rect x="8" y="8" width="3" height="3"/><rect x="13" y="8" width="3" height="3"/><rect x="8" y="13" width="8" height="3"/>`
  },

  // Edit pencil (filled, 16x16)
  editFilled: {
    viewBox: '0 0 16 16',
    path: `<path d="M12.146 1.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-9 9a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l9-9z"/>`
  },

  // Crop/selection icon (16x16 - L-shaped corners forming selection box)
  crop: {
    viewBox: '0 0 16 16',
    path: `<rect x="1" y="3" width="2" height="10"/><rect x="1" y="11" width="6" height="2"/><rect x="13" y="3" width="2" height="10"/><rect x="9" y="3" width="6" height="2"/><rect x="5" y="7" width="6" height="2"/>`
  },

  // Close icon
  close: {
    viewBox: '0 0 12 12',
    path: 'M2 2l8 8M10 2l-8 8',
    stroke: true,
    strokeWidth: 2,
    fill: 'none'
  },

  // Plus icon
  plus: {
    viewBox: '0 0 12 12',
    path: 'M6 1v10M1 6h10',
    stroke: true,
    strokeWidth: 2,
    fill: 'none'
  },

  // Check icon
  check: {
    viewBox: '0 0 12 12',
    path: 'M2 6l3 3 5-6',
    stroke: true,
    strokeWidth: 2,
    fill: 'none'
  },

  // History/clock icon
  history: {
    viewBox: '0 0 16 16',
    path: `<circle cx="8" cy="8" r="6" fill="none"/><line x1="8" y1="5" x2="8" y2="8"/><line x1="8" y1="8" x2="10.5" y2="9.5"/>`,
    stroke: true,
    strokeWidth: 1.5,
    fill: 'none'
  },

  // Duplicate icon (two overlapping rectangles)
  duplicate: {
    viewBox: '0 0 16 16',
    path: `<rect x="1" y="4" width="9" height="11"/><rect x="4" y="1" width="9" height="11"/>`,
    stroke: true,
    strokeWidth: 1.5,
    fill: 'none'
  },

  // Play triangle icon (for video badges)
  play: {
    viewBox: '0 0 16 16',
    path: `<polygon points="4,2 14,8 4,14"/>`
  },

  // View/eye icon (circle with inner dot)
  viewIdea: {
    viewBox: '0 0 12 12',
    path: `<circle cx="6" cy="6" r="5"/><circle cx="6" cy="6" r="2"/>`,
    stroke: true,
    strokeWidth: 1.5,
    fill: 'none'
  },

  // Download icon (16x16 pixel style for idea-canvas)
  download16: {
    viewBox: '0 0 16 16',
    path: `<rect x="7" y="1" width="2" height="8"/><rect x="4" y="6" width="2" height="2"/><rect x="10" y="6" width="2" height="2"/><rect x="5" y="7" width="2" height="2"/><rect x="9" y="7" width="2" height="2"/><rect x="2" y="11" width="12" height="2"/><rect x="2" y="13" width="2" height="2"/><rect x="12" y="13" width="2" height="2"/>`
  }
};

// ============================================================================
// ICON GENERATION FUNCTIONS
// ============================================================================

/**
 * Get trigger icon (Unicode emoji)
 * @param {string} code - Trigger code (e.g., 'A1', 'B2')
 * @returns {string} - HTML entity for the icon
 */
export function getTriggerIcon(code) {
  return TRIGGER_ICONS[code] || TRIGGER_ICONS.DEFAULT;
}

/**
 * Get format icon path (PNG image)
 * @param {string} format - Format name (e.g., 'print', 'video')
 * @param {boolean} mini - Whether to get mini icon
 * @returns {string} - Path to the icon image
 */
export function getFormatIconPath(format, mini = false) {
  const paths = mini ? FORMAT_MINI_ICON_PATHS : FORMAT_ICON_PATHS;
  return paths[format] || paths['print'];
}

/**
 * Generate SVG icon HTML
 * @param {string} name - Icon name from SVG_ICONS
 * @param {Object} options - Customization options
 * @param {number} [options.size] - Icon size (sets both width and height)
 * @param {number} [options.width] - Icon width
 * @param {number} [options.height] - Icon height
 * @param {string} [options.className] - CSS class name
 * @param {string} [options.fill] - Fill color (default: currentColor)
 * @param {string} [options.stroke] - Stroke color (default: currentColor)
 * @returns {string} - SVG HTML string
 */
export function getIcon(name, options = {}) {
  const icon = SVG_ICONS[name];
  if (!icon) {
    console.warn(`[Icons] Unknown icon: ${name}`);
    return '';
  }

  const {
    size = null,
    width = size,
    height = size,
    className = '',
    fill = 'currentColor',
    stroke = 'currentColor',
  } = options;

  const sizeAttr = width && height ? `width="${width}" height="${height}"` : '';
  const classAttr = className ? `class="${className}"` : '';

  if (icon.stroke) {
    const strokeWidth = icon.strokeWidth || 2;
    const pathContent = icon.path.startsWith('<') ? icon.path : `<path d="${icon.path}"/>`;
    return `<svg viewBox="${icon.viewBox}" ${sizeAttr} ${classAttr} fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="square">${pathContent}</svg>`;
  }

  return `<svg viewBox="${icon.viewBox}" ${sizeAttr} ${classAttr} fill="${fill}">${icon.path}</svg>`;
}

/**
 * Get format icon as SVG (for presentation mode)
 * @param {string} format - Format name
 * @param {Object} options - Icon options
 * @returns {string} - SVG HTML string
 */
export function getFormatIconSvg(format, options = {}) {
  const iconMap = {
    print: 'formatPrint',
    video: 'formatVideo',
    social: 'formatSocial',
    experiential: 'formatExperiential',
    digital: 'formatDigital',
    campaign: 'formatCampaign'
  };
  return getIcon(iconMap[format] || 'formatPrint', options);
}

// ============================================================================
// CONVENIENCE OBJECT
// ============================================================================

/**
 * Icons object for convenient access
 * Usage: Icons.copy({ size: 20 })
 */
export const Icons = {
  // Action buttons (20x20)
  canvasView: (opts) => getIcon('canvasView', opts),
  storyboardGrid: (opts) => getIcon('storyboardGrid', opts),
  copy: (opts) => getIcon('copy', opts),
  save: (opts) => getIcon('save', opts),
  delete: (opts) => getIcon('delete', opts),
  bulb: (opts) => getIcon('bulb', opts),
  close: (opts) => getIcon('close', opts),
  plus: (opts) => getIcon('plus', opts),
  check: (opts) => getIcon('check', opts),
  history: (opts) => getIcon('history', opts),
  duplicate: (opts) => getIcon('duplicate', opts),
  play: (opts) => getIcon('play', opts),
  viewIdea: (opts) => getIcon('viewIdea', opts),
  download16: (opts) => getIcon('download16', opts),

  // Image/video actions (16x16)
  regenerate: (opts) => getIcon('regenerate', opts),
  fullscreen16: (opts) => getIcon('fullscreen16', opts),
  makeVideo: (opts) => getIcon('makeVideo', opts),
  generateImage: (opts) => getIcon('generateImage', opts),
  editFilled: (opts) => getIcon('editFilled', opts),
  crop: (opts) => getIcon('crop', opts),

  // Small actions (12x12)
  download: (opts) => getIcon('download', opts),
  fullscreen: (opts) => getIcon('fullscreen', opts),
  refresh: (opts) => getIcon('refresh', opts),
  edit: (opts) => getIcon('edit', opts),
  video: (opts) => getIcon('video', opts),
  image: (opts) => getIcon('image', opts),
  arrowLeft: (opts) => getIcon('arrowLeft', opts),
  arrowRight: (opts) => getIcon('arrowRight', opts),

  // Formats
  format: (format, opts) => getFormatIconSvg(format, opts),
  formatPath: (format, mini) => getFormatIconPath(format, mini),

  // Triggers
  trigger: (code) => getTriggerIcon(code),

  // Presentation format icons (outline style)
  formatOutline: (format, opts) => {
    const iconMap = {
      print: 'formatPrintOutline',
      video: 'formatVideoOutline',
      social: 'formatSocialOutline',
      experiential: 'formatExperientialOutline',
      digital: 'formatDigitalOutline',
      campaign: 'formatCampaignOutline'
    };
    return getIcon(iconMap[format] || 'formatPrintOutline', opts);
  }
};

// ============================================================================
// GLOBAL EXPORTS (for non-module usage)
// ============================================================================

if (typeof window !== 'undefined') {
  window.Icons = Icons;
  window.getTriggerIcon = getTriggerIcon;
  window.getFormatIconPath = getFormatIconPath;
  window.getIcon = getIcon;
  window.getFormatIconSvg = getFormatIconSvg;
}

export default Icons;
